import React, { useMemo, useState } from "react";
import { Mafs, Coordinates, Point, Line, Polygon, Theme } from "mafs";
import {
    Card,
    Space,
    Typography,
    Segmented,
    Tag,
    Alert,
    Row,
    Divider,
    Statistic,
    Col,
} from "antd";
import {
    UserOutlined,
    CheckCircleOutlined,
    BulbOutlined,
    FallOutlined,
    RiseOutlined,
} from "@ant-design/icons";

const { Title, Text: AntText, Paragraph } = Typography;

export const CenterVariability = ({ config, content }) => {
    const cfg = config || {};
    const explanation =
        content?.explanation || "Explore measures of center and variability.";

    const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
    const dp = Number.isInteger(cfg.precisionDp) ? clamp(cfg.precisionDp, 0, 4) : 1;
    const format = (n) => Number(Number(n).toFixed(dp));

    const [viewMode, setViewMode] = useState("CORRECT");
    const hasStudent = Array.isArray(cfg.studentData) && cfg.studentData.length > 0;

    const plotType = cfg.plotType || "DOT_PLOT";
    const rangeMin = Number.isFinite(cfg.rangeMin) ? cfg.rangeMin : 0;
    const rangeMax = Number.isFinite(cfg.rangeMax) ? cfg.rangeMax : 20;

    const showMean = cfg.showMean !== false;
    const showMedian = cfg.showMedian !== false;

    // --- Helper for Stats Calculation ---
    const calcStats = (rawData) => {
        const d = (rawData || [])
            .map(Number)
            .filter(Number.isFinite)
            .sort((a, b) => a - b);

        if (d.length === 0) return null;

        const mean = d.reduce((a, b) => a + b, 0) / d.length;

        const mid = Math.floor(d.length / 2);
        const median =
            d.length % 2 === 0 ? (d[mid - 1] + d[mid]) / 2 : d[mid];

        const q = (p) => {
            const idx = (d.length - 1) * p;
            const lo = Math.floor(idx);
            const hi = Math.ceil(idx);
            if (lo === hi) return d[lo];
            const w = idx - lo;
            return d[lo] * (1 - w) + d[hi] * w;
        };

        const q1 = q(0.25);
        const q3 = q(0.75);
        const iqr = q3 - q1;

        return {
            data: d,
            mean,
            median,
            q1,
            q3,
            iqr,
            min: d[0],
            max: d[d.length - 1],
        };
    };

    const correctStats = useMemo(() => calcStats(cfg.data || []), [cfg.data]);
    const studentStats = useMemo(
        () => calcStats(cfg.studentData || []),
        [cfg.studentData]
    );
    const active = viewMode === "STUDENT" ? studentStats : correctStats;

    // --- Multiset Diff on DISPLAY BUCKETS (precisionDp) ---
    const diffLayers = useMemo(() => {
        if (!hasStudent || !correctStats || !studentStats) {
            return { missing: [], extra: [], common: (active?.data || []).map(format) };
        }

        const bucketCounts = (arr) => {
            const m = new Map();
            for (const v of arr) {
                const b = format(v);
                m.set(b, (m.get(b) || 0) + 1);
            }
            return m;
        };

        const cMap = bucketCounts(correctStats.data);
        const sMap = bucketCounts(studentStats.data);

        const missing = []; // in correct not student
        const extra = []; // in student not correct
        const common = []; // in both

        const allKeys = new Set([...cMap.keys(), ...sMap.keys()]);
        for (const k of allKeys) {
            const c = cMap.get(k) || 0;
            const s = sMap.get(k) || 0;

            const commonCount = Math.min(c, s);
            for (let i = 0; i < commonCount; i++) common.push(k);

            if (s > c) for (let i = 0; i < s - commonCount; i++) extra.push(k);
            if (c > s) for (let i = 0; i < c - commonCount; i++) missing.push(k);
        }

        return { missing, extra, common };
    }, [hasStudent, correctStats, studentStats, dp]); // dp impacts format()

    // --- Outlier Detection (1.5*IQR) on active dataset ---
    const outliers = useMemo(() => {
        if (!active) return [];
        if (!Number.isFinite(active.iqr) || active.iqr <= 0) return [];
        const threshold = 1.5 * active.iqr;
        return active.data.filter(
            (v) => v < active.q1 - threshold || v > active.q3 + threshold
        );
    }, [active]);

    // --- Stacking logic for dots: stack by bucket and draw at bucket x ---
    const renderDots = (vals, color, opacity = 1, yOffset = 0) => {
        const stack = new Map();
        return (vals || []).map((v, i) => {
            const b = Number(v); // vals are bucketed numbers already (or we pass bucketed)
            const count = stack.get(b) || 0;
            stack.set(b, count + 1);

            return (
                <Point
                    key={`${String(color)}-${i}`}
                    x={b}
                    y={count * 0.4 + yOffset}
                    color={color}
                    opacity={opacity}
                />
            );
        });
    };

    const showBox = String(plotType).includes("BOX") && !!active;

    const meanX = active?.mean;
    const medianX = active?.median;

    return (
        <div className="ch-center-variability-widget animate-slide-up">
            <Space orientation="vertical" style={{ width: "100%" }} size="middle">
                <Card size="small" className="ch-shapeshifter-header">
                    <Row justify="space-between" align="middle">
                        <Space>
                            <Title level={4} style={{ margin: 0 }}>
                                Center &amp; Variability
                            </Title>

                            {hasStudent && (
                                <Segmented
                                    value={viewMode}
                                    onChange={setViewMode}
                                    options={[
                                        {
                                            label: "Correct",
                                            value: "CORRECT",
                                            icon: <CheckCircleOutlined />,
                                        },
                                        { label: "Yours", value: "STUDENT", icon: <UserOutlined /> },
                                    ]}
                                />
                            )}
                        </Space>

                        <Tag color="blue">{String(plotType).replace(/_/g, " ")}</Tag>
                    </Row>
                </Card>

                <Paragraph type="secondary" style={{ margin: 0 }}>
                    {explanation}
                </Paragraph>

                <div className="ch-statistics-layout">
                    <div className="ch-statistics-stage">
                        <Mafs
                            height={400}
                            viewBox={{ x: [rangeMin, rangeMax], y: [-1, 8] }}
                            pan={false}
                            zoom={false}
                        >
                            <Coordinates.Cartesian />

                            {/* STUDENT view: diff layers */}
                            {viewMode === "STUDENT" ? (
                                <>
                                    {/* common = orange */}
                                    {renderDots(diffLayers.common, Theme.orange, 0.9)}

                                    {/* extra = indigo (distinct) */}
                                    {renderDots(diffLayers.extra, Theme.indigo, 1, 0.05)}

                                    {/* missing = faint green */}
                                    {renderDots(diffLayers.missing, Theme.green, 0.2)}
                                </>
                            ) : (
                                // CORRECT view: bucket the data for consistent stacking
                                renderDots((active?.data || []).map(format), Theme.blue)
                            )}

                            {/* Box plot (minimal rectangle only) */}
                            {showBox && (
                                <Polygon
                                    points={[
                                        [active.q1, 4],
                                        [active.q3, 4],
                                        [active.q3, 5],
                                        [active.q1, 5],
                                    ]}
                                    color={Theme.green}
                                    opacity={0.1}
                                />
                            )}

                            {/* Mean / Median lines (guarded + respects toggles) */}
                            {showMean && Number.isFinite(meanX) && (
                                <Line.Segment
                                    point1={[meanX, -0.5]}
                                    point2={[meanX, 7]}
                                    color={Theme.orange}
                                    weight={3}
                                />
                            )}

                            {showMedian && Number.isFinite(medianX) && (
                                <Line.Segment
                                    point1={[medianX, -0.5]}
                                    point2={[medianX, 7]}
                                    color={Theme.red}
                                    weight={3}
                                    opacity={0.5}
                                />
                            )}
                        </Mafs>
                    </div>

                    <div className="ch-statistics-panel">
                        <Space orientation="vertical" style={{ width: "100%" }}>
                            {/* Outlier chip */}
                            {outliers.length > 0 && (
                                <Alert
                                    message="Outlier Detected"
                                    description={`Value(s) ${outliers
                                        .map(format)
                                        .join(", ")} can shift the mean more than the median.`}
                                    type="warning"
                                    showIcon
                                />
                            )}

                            <Card size="small" title="Summary Stats">
                                <Row gutter={[8, 8]}>
                                    <Col span={12}>
                                        <Statistic
                                            title="Mean"
                                            value={
                                                Number.isFinite(active?.mean) ? format(active.mean) : "—"
                                            }
                                        />
                                    </Col>
                                    <Col span={12}>
                                        <Statistic
                                            title="Median"
                                            value={
                                                Number.isFinite(active?.median)
                                                    ? format(active.median)
                                                    : "—"
                                            }
                                        />
                                    </Col>
                                </Row>
                            </Card>

                            {/* Delta panel */}
                            {hasStudent && correctStats && studentStats && (
                                <Card size="small" title="The Delta (Change)" className="ch-delta-card">
                                    <DeltaRow
                                        label="Mean"
                                        cur={studentStats.mean}
                                        prev={correctStats.mean}
                                        format={format}
                                    />
                                    <DeltaRow
                                        label="Median"
                                        cur={studentStats.median}
                                        prev={correctStats.median}
                                        format={format}
                                    />
                                    <DeltaRow
                                        label="IQR"
                                        cur={studentStats.iqr}
                                        prev={correctStats.iqr}
                                        format={format}
                                    />
                                </Card>
                            )}

                            <Alert
                                message="Tip"
                                description="Mean is sensitive to outliers; median is more robust. The box plot summarises spread using Q1–Q3."
                                type="info"
                                showIcon
                                icon={<BulbOutlined />}
                            />
                        </Space>
                    </div>
                </div>
            </Space>
        </div>
    );
};

const DeltaRow = ({ label, cur, prev, format }) => {
    const c = Number(cur);
    const p = Number(prev);
    const ok = Number.isFinite(c) && Number.isFinite(p);

    const diff = ok ? c - p : NaN;
    const isIncrease = ok && diff > 0;

    return (
        <div className="flexSpaceBetween" style={{ marginBottom: 4 }}>
            <Typography.Text type="secondary">{label}</Typography.Text>
            <Space>
                <Typography.Text strong>{ok ? format(c) : "—"}</Typography.Text>

                {ok && Math.abs(diff) > 1e-6 && (
                    <Tag
                        color={isIncrease ? "orange" : "cyan"}
                        icon={isIncrease ? <RiseOutlined /> : <FallOutlined />}
                    >
                        {isIncrease ? "+" : ""}
                        {format(diff)}
                    </Tag>
                )}
            </Space>
        </div>
    );
};
